import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { userService } from '../services/user';
import { printConfigurationService } from '../services/printConfiguration';
import { organizationService } from '../services/organization';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { User, RegisterRequest, PrintConfiguration } from '../types';
import {
  Settings as SettingsIcon,
  Users,
  UserPlus,
  X,
  Save,
  Trash2,
  UserX,
  UserCheck,
  Mail,
  Phone,
  Calendar,
  Building2,
  Eye,
  EyeOff,
  Printer,
  Plus,
  Edit,
  Check,
  Bell,
  Send,
} from 'lucide-react';
import { webFcmService } from '../services/fcmService';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { SkeletonGrid, ButtonLoader } from '../components/LoadingStates';
import { ConfirmationModal } from '../components/ConfirmationModal';

interface RegisterUserForm extends RegisterRequest { }

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const activeRestaurantId = getActiveRestaurantId();
  const [activeTab, setActiveTab] = useState<'users' | 'print-config' | 'general'>('users');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Print configuration states
  const [showPrintConfigModal, setShowPrintConfigModal] = useState(false);
  const [editingPrintConfig, setEditingPrintConfig] = useState<PrintConfiguration | null>(null);

  // Push notification state
  const { data: availableRoles = [] } = useQuery(
    ['roles', user?.organizationCode],
    () => organizationService.getRolesByOrgCode(user!.organizationCode!),
    { enabled: !!user?.organizationCode },
  );
  const [pushRoles, setPushRoles] = useState<string[]>([]);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushSending, setPushSending] = useState(false);
  const pushBodyRef = useRef<HTMLTextAreaElement>(null);

  // Unified confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'warning' | 'danger' | 'info';
    confirmText: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    type: 'warning',
    confirmText: 'Confirm'
  });

  // Fetch active users
  const { data: users, isLoading: usersLoading } = useQuery(
    ['users', user?.organizationCode],
    () => userService.getActiveUsers(user?.organizationCode),
    {
      enabled: !!user?.organizationCode && activeTab === 'users',
    }
  );

  // Form handling for user registration
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<RegisterUserForm>();

  // Register user mutation
  const registerUserMutation = useMutation(
    (data: RegisterRequest) => userService.registerUser(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
        setShowRegisterModal(false);
        reset();
        setShowPassword(false);
        toast.success('User registered successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to register user');
      }
    }
  );

  // Deactivate user mutation
  const deactivateUserMutation = useMutation(
    (userId: string) => userService.deactivateUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        toast.success('User deactivated successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to deactivate user');
      }
    }
  );

  // Activate user mutation
  const activateUserMutation = useMutation(
    (userId: string) => userService.activateUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        toast.success('User activated successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to activate user');
      }
    }
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    (userId: string) => userService.deleteUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        toast.success('User deleted successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to delete user');
      }
    }
  );

  // Fetch print configurations
  const { data: printConfigs, isLoading: printConfigsLoading } = useQuery(
    ['printConfigs', user?.organizationCode, activeRestaurantId],
    () => printConfigurationService.getAll(user?.organizationCode!, activeRestaurantId),
    {
      enabled: !!user?.organizationCode && activeTab === 'print-config',
    }
  );

  // Print configuration form
  const {
    register: registerPrintConfig,
    handleSubmit: handleSubmitPrintConfig,
    reset: resetPrintConfig,
    setValue: setPrintConfigValue,
    watch: watchPrintConfig,
    formState: { errors: printConfigErrors, isSubmitting: isPrintConfigSubmitting }
  } = useForm<PrintConfiguration>();

  // Watch printType to conditionally show KOT-specific options
  const selectedPrintType = watchPrintConfig('printType');

  // Create/Update print configuration mutation
  const savePrintConfigMutation = useMutation(
    (data: PrintConfiguration) => {
      const configData = {
        ...data,
        organizationCode: user?.organizationCode!,
        restaurantId: activeRestaurantId || undefined
      };

      if (editingPrintConfig?.id) {
        return printConfigurationService.update(editingPrintConfig.id, configData);
      } else {
        return printConfigurationService.create(configData);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['printConfigs']);
        setShowPrintConfigModal(false);
        setEditingPrintConfig(null);
        resetPrintConfig();
        toast.success(editingPrintConfig ? 'Print configuration updated successfully!' : 'Print configuration created successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to save print configuration');
      }
    }
  );

  // Delete print configuration mutation
  const deletePrintConfigMutation = useMutation(
    (id: string) => printConfigurationService.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['printConfigs']);
        setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        toast.success('Print configuration deleted successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to delete print configuration');
      }
    }
  );

  const onSubmitRegister = (data: RegisterUserForm) => {
    registerUserMutation.mutate({
      ...data,
      organizationCode: user?.organizationCode!
    });
  };

  const handleDeactivateUser = (userToDeactivate: User) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Deactivate User',
      message: `Are you sure you want to deactivate ${userToDeactivate.firstName} ${userToDeactivate.lastName}? This user will no longer be able to access the system.`,
      onConfirm: () => {
        deactivateUserMutation.mutate(userToDeactivate.id);
      },
      type: 'warning',
      confirmText: 'Deactivate'
    });
  };

  const handleActivateUser = (userToActivate: User) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Activate User',
      message: `Are you sure you want to activate ${userToActivate.firstName} ${userToActivate.lastName}? This user will be able to access the system again.`,
      onConfirm: () => {
        activateUserMutation.mutate(userToActivate.id);
      },
      type: 'info',
      confirmText: 'Activate'
    });
  };

  const handleDeleteUser = (userToDelete: User) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to permanently delete ${userToDelete.firstName} ${userToDelete.lastName}? This action cannot be undone.`,
      onConfirm: () => {
        deleteUserMutation.mutate(userToDelete.id);
      },
      type: 'danger',
      confirmText: 'Delete'
    });
  };

  const onSubmitPrintConfig = (data: PrintConfiguration) => {
    // Check if trying to create a duplicate print type (only when creating new, not editing)
    if (!editingPrintConfig && printConfigs) {
      const existingConfig = printConfigs.find(config => config.printType === data.printType);
      if (existingConfig) {
        toast.error(`A configuration for ${data.printType} already exists. Please edit the existing one.`);
        return;
      }
    }
    savePrintConfigMutation.mutate(data);
  };

  const handleEditPrintConfig = (config: PrintConfiguration) => {
    setEditingPrintConfig(config);
    setPrintConfigValue('printType', config.printType);
    setPrintConfigValue('printSize', config.printSize);
    setPrintConfigValue('restaurantName', config.restaurantName);
    setPrintConfigValue('restaurantAddress', config.restaurantAddress);
    setPrintConfigValue('restaurantPhone', config.restaurantPhone);
    setPrintConfigValue('gstNumber', config.gstNumber);
    setPrintConfigValue('logoUrl', config.logoUrl);
    setPrintConfigValue('singleKotForAllKitchens', config.singleKotForAllKitchens || false);
    setPrintConfigValue('gstApplicable', config.gstApplicable !== false);
    setPrintConfigValue('upiId', config.upiId || '');
    setShowPrintConfigModal(true);
  };

  const handleDeletePrintConfig = (config: PrintConfiguration) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Print Configuration',
      message: `Are you sure you want to delete the ${config.printType === 'KOT' ? 'Kitchen Order Ticket' : 'Customer Bill'} configuration? This action cannot be undone.`,
      onConfirm: () => {
        deletePrintConfigMutation.mutate(config.id);
      },
      type: 'danger',
      confirmText: 'Delete'
    });
  };

  // Helper to check if both configurations exist
  const hasKOTConfig = printConfigs?.some(config => config.printType === 'KOT');
  const hasBillConfig = printConfigs?.some(config => config.printType === 'BILL');
  const canAddNewConfig = !hasKOTConfig || !hasBillConfig;

  return (
    <div className="p-3 sm:p-4 md:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
          <SettingsIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
          Settings
        </h1>
        <p className="text-xs sm:text-sm mt-1" style={{ color: currentTheme.colors.text.secondary }}>
          Manage your application settings and users
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-4 sm:mb-6">
        <div className="border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
          <div className="flex space-x-4 sm:space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('users')}
              className={`pb-3 sm:pb-4 px-2 font-medium transition-colors relative whitespace-nowrap text-sm sm:text-base ${activeTab === 'users' ? 'border-b-2' : ''
                }`}
              style={{
                color: activeTab === 'users' ? currentTheme.colors.primary[600] : currentTheme.colors.text.secondary,
                borderColor: activeTab === 'users' ? currentTheme.colors.primary[600] : 'transparent'
              }}
            >
              <Users className="h-4 w-4 sm:h-5 sm:w-5 inline mr-2" />
              Manage Users
            </button>
            <button
              onClick={() => setActiveTab('print-config')}
              className={`pb-3 sm:pb-4 px-2 font-medium transition-colors relative whitespace-nowrap text-sm sm:text-base ${activeTab === 'print-config' ? 'border-b-2' : ''
                }`}
              style={{
                color: activeTab === 'print-config' ? currentTheme.colors.primary[600] : currentTheme.colors.text.secondary,
                borderColor: activeTab === 'print-config' ? currentTheme.colors.primary[600] : 'transparent'
              }}
            >
              <Printer className="h-4 w-4 sm:h-5 sm:w-5 inline mr-2" />
              Print Configuration
            </button>
            <button
              onClick={() => setActiveTab('general')}
              className={`pb-3 sm:pb-4 px-2 font-medium transition-colors relative whitespace-nowrap text-sm sm:text-base ${activeTab === 'general' ? 'border-b-2' : ''
                }`}
              style={{
                color: activeTab === 'general' ? currentTheme.colors.primary[600] : currentTheme.colors.text.secondary,
                borderColor: activeTab === 'general' ? currentTheme.colors.primary[600] : 'transparent'
              }}
            >
              <SettingsIcon className="h-4 w-4 sm:h-5 sm:w-5 inline mr-2" />
              General
            </button>
          </div>
        </div>
      </div>

      {/* Manage Users Tab */}
      {activeTab === 'users' && (
        <div>
          {/* Header with Add User Button */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                User Management
              </h2>
              <p className="text-xs sm:text-sm mt-1" style={{ color: currentTheme.colors.text.secondary }}>
                Add, view, and manage users in your organization
              </p>
            </div>
            <button
              onClick={() => setShowRegisterModal(true)}
              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 text-sm sm:text-base text-white rounded-lg transition-colors w-full sm:w-auto"
              style={{ backgroundColor: currentTheme.colors.primary[600] }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add New User
            </button>
          </div>

          {/* Users List */}
          {usersLoading ? (
            <SkeletonGrid items={6} columns={3} />
          ) : users && users.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {users.map((userItem) => (
                <div
                  key={userItem.id}
                  className="rounded-lg sm:rounded-xl border shadow-sm p-4 sm:p-6"
                  style={{
                    backgroundColor: currentTheme.colors.background.card,
                    borderColor: currentTheme.colors.border.primary
                  }}
                >
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <div
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: currentTheme.colors.primary[100] }}
                      >
                        <Users className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: currentTheme.colors.primary[600] }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-sm sm:text-base truncate" style={{ color: currentTheme.colors.text.primary }}>
                          {`${userItem.firstName}${userItem.middleName ? ` ${userItem.middleName}` : ''} ${userItem.lastName}`}
                        </h3>
                        {userItem.active !== undefined && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${userItem.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}
                          >
                            {userItem.active ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-3 sm:mb-4">
                    <div className="flex items-center text-xs sm:text-sm min-w-0" style={{ color: currentTheme.colors.text.secondary }}>
                      <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{userItem.email}</span>
                    </div>
                    <div className="flex items-center text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                      <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                      {userItem.mobile}
                    </div>
                    <div className="flex items-center text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                      {userItem.dob ? format(new Date(userItem.dob), 'MMM dd, yyyy') : '—'}
                    </div>
                    {userItem.createdAt && (
                      <div className="flex items-center text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                        <Building2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                        Joined {format(new Date(userItem.createdAt), 'MMM dd, yyyy')}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 pt-3 sm:pt-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                    {userItem.active ? (
                      <button
                        onClick={() => handleDeactivateUser(userItem)}
                        disabled={userItem.id === user?.id}
                        className="flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: '#fef2f2',
                          color: '#dc2626'
                        }}
                        onMouseEnter={(e) => (!userItem || (userItem.id !== user?.id)) && (e.currentTarget.style.backgroundColor = '#fee2e2')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fef2f2')}
                        title={userItem.id === user?.id ? 'Cannot deactivate yourself' : 'Deactivate user'}
                      >
                        <UserX className="h-4 w-4 mr-1" />
                        Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivateUser(userItem)}
                        className="flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center text-sm font-medium"
                        style={{
                          backgroundColor: '#dcfce7',
                          color: '#166534'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#bbf7d0')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#dcfce7')}
                        title="Activate user"
                      >
                        <UserCheck className="h-4 w-4 mr-1" />
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteUser(userItem)}
                      disabled={userItem.id === user?.id}
                      className="flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: '#fef2f2',
                        color: '#dc2626'
                      }}
                      onMouseEnter={(e) => (!userItem || (userItem.id !== user?.id)) && (e.currentTarget.style.backgroundColor = '#fee2e2')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fef2f2')}
                      title={userItem.id === user?.id ? 'Cannot delete yourself' : 'Delete user'}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border p-12 text-center shadow-sm" style={{
              backgroundColor: currentTheme.colors.background.card,
              borderColor: currentTheme.colors.border.primary
            }}>
              <Users className="h-16 w-16 mx-auto mb-4" style={{ color: currentTheme.colors.text.secondary }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: currentTheme.colors.text.primary }}>
                No Users Found
              </h3>
              <p className="mb-6" style={{ color: currentTheme.colors.text.secondary }}>
                Get started by adding your first user to the system.
              </p>
              <button
                onClick={() => setShowRegisterModal(true)}
                className="text-white px-6 py-3 rounded-lg transition-colors inline-flex items-center font-semibold"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
              >
                <UserPlus className="h-5 w-5 mr-2" />
                Add New User
              </button>
            </div>
          )}
        </div>
      )}

      {/* Print Configuration Tab */}
      {activeTab === 'print-config' && (
        <div>
          {/* Header with Add Button */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                Print Configuration
              </h2>
              <p className="text-xs sm:text-sm mt-1" style={{ color: currentTheme.colors.text.secondary }}>
                Configure printer settings for KOT and Bill printing (maximum 2 configurations: 1 KOT and 1 BILL)
              </p>
              {!canAddNewConfig && (
                <p className="text-xs mt-1 font-medium text-green-600">
                  All configurations are set up. You can edit or delete existing ones.
                </p>
              )}
            </div>
            <button
              onClick={() => {
                setEditingPrintConfig(null);
                resetPrintConfig();
                setShowPrintConfigModal(true);
              }}
              disabled={!canAddNewConfig}
              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 text-sm sm:text-base text-white rounded-lg transition-colors w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: currentTheme.colors.primary[600] }}
              onMouseEnter={(e) => canAddNewConfig && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
              onMouseLeave={(e) => canAddNewConfig && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
              title={!canAddNewConfig ? 'Both KOT and BILL configurations already exist' : 'Add new configuration'}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Configuration
            </button>
          </div>

          {/* Print Configurations List */}
          {printConfigsLoading ? (
            <SkeletonGrid items={4} columns={2} />
          ) : printConfigs && printConfigs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {printConfigs.map((config) => (
                <div
                  key={config.id}
                  className="rounded-lg sm:rounded-xl border shadow-sm p-4 sm:p-6"
                  style={{
                    backgroundColor: currentTheme.colors.background.card,
                    borderColor: currentTheme.colors.border.primary
                  }}
                >
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <div
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: currentTheme.colors.primary[100] }}
                      >
                        <Printer className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: currentTheme.colors.primary[600] }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-sm sm:text-base truncate" style={{ color: currentTheme.colors.text.primary }}>
                          {config.printType === 'KOT' ? 'Kitchen Order Ticket' : 'Customer Bill'}
                        </h3>
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 bg-blue-100 text-blue-800"
                        >
                          Size : {config.printSize.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-3 sm:mb-4">
                    {config.restaurantName && (
                      <div className="flex items-center text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                        <Building2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{config.restaurantName}</span>
                      </div>
                    )}
                    {config.restaurantPhone && (
                      <div className="flex items-center text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                        <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                        {config.restaurantPhone}
                      </div>
                    )}
                    {config.restaurantAddress && (
                      <div className="flex items-start text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                        <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2">{config.restaurantAddress}</span>
                      </div>
                    )}
                    {config.gstNumber && (
                      <div className="flex items-center text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                        <Check className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                        GST: {config.gstNumber}
                      </div>
                    )}
                    {config.printType === 'KOT' && (
                      <div className="flex items-center text-xs sm:text-sm mt-2" style={{ color: currentTheme.colors.text.secondary }}>
                        <Printer className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                        KOT Mode: {config.singleKotForAllKitchens ? 'Single KOT for all kitchens' : 'Separate KOT per kitchen'}
                      </div>
                    )}
                    {config.printType === 'BILL' && (
                      <div className="flex items-center text-xs sm:text-sm mt-2" style={{ color: currentTheme.colors.text.secondary }}>
                        <Check className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                        GST in Bill: {config.gstApplicable !== false ? 'Enabled' : 'Disabled'}
                      </div>
                    )}
                    {config.printType === 'BILL' && config.upiId && (
                      <div className="flex items-center text-xs sm:text-sm mt-2" style={{ color: currentTheme.colors.text.secondary }}>
                        <Check className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                        UPI ID: {config.upiId}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 pt-3 sm:pt-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                    <button
                      onClick={() => handleEditPrintConfig(config)}
                      className="flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center text-sm font-medium"
                      style={{
                        backgroundColor: currentTheme.colors.primary[100],
                        color: currentTheme.colors.primary[700]
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[200])}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[100])}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeletePrintConfig(config)}
                      className="flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center text-sm font-medium"
                      style={{
                        backgroundColor: '#fef2f2',
                        color: '#dc2626'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fee2e2')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fef2f2')}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border p-12 text-center shadow-sm" style={{
              backgroundColor: currentTheme.colors.background.card,
              borderColor: currentTheme.colors.border.primary
            }}>
              <Printer className="h-16 w-16 mx-auto mb-4" style={{ color: currentTheme.colors.text.secondary }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: currentTheme.colors.text.primary }}>
                No Print Configurations Found
              </h3>
              <p className="mb-6" style={{ color: currentTheme.colors.text.secondary }}>
                Get started by adding your first print configuration for KOT or Bill.
              </p>
              <button
                onClick={() => {
                  setEditingPrintConfig(null);
                  resetPrintConfig();
                  setShowPrintConfigModal(true);
                }}
                disabled={!canAddNewConfig}
                className="text-white px-6 py-3 rounded-lg transition-colors inline-flex items-center font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
                onMouseEnter={(e) => canAddNewConfig && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                onMouseLeave={(e) => canAddNewConfig && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                title={!canAddNewConfig ? 'Both KOT and BILL configurations already exist' : 'Add new configuration'}
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Configuration
              </button>
            </div>
          )}
        </div>
      )}

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          {/* Push Notifications Card */}
          <div className="rounded-xl border shadow-sm" style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}>
            <div className="p-5 border-b flex items-center gap-3" style={{ borderColor: currentTheme.colors.border.primary }}>
              <Bell className="h-5 w-5" style={{ color: currentTheme.colors.primary[600] }} />
              <h3 className="text-base font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                Send Push Notification
              </h3>
            </div>
            <div className="p-5 space-y-4">
              {/* Role selector */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                  Target Roles <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableRoles.map(role => (
                    <button
                      key={role.key}
                      type="button"
                      onClick={() => setPushRoles(prev =>
                        prev.includes(role.key) ? prev.filter(r => r !== role.key) : [...prev, role.key]
                      )}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors"
                      style={{
                        backgroundColor: pushRoles.includes(role.key) ? currentTheme.colors.primary[600] : currentTheme.colors.background.secondary,
                        color: pushRoles.includes(role.key) ? '#fff' : currentTheme.colors.text.primary,
                        borderColor: pushRoles.includes(role.key) ? currentTheme.colors.primary[600] : currentTheme.colors.border.primary,
                      }}
                    >
                      {pushRoles.includes(role.key) && <Check className="inline h-3 w-3 mr-1" />}
                      {role.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs mt-1" style={{ color: currentTheme.colors.text.secondary }}>
                  Notification will be sent to all users with any of the selected roles in the active restaurant.
                </p>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.primary }}>
                  Notification Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={pushTitle}
                  onChange={e => setPushTitle(e.target.value)}
                  placeholder="e.g. Special Offer Today!"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 outline-none"
                  style={{
                    backgroundColor: currentTheme.colors.background.secondary,
                    borderColor: currentTheme.colors.border.primary,
                    color: currentTheme.colors.text.primary,
                  }}
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.primary }}>
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  ref={pushBodyRef}
                  value={pushBody}
                  onChange={e => setPushBody(e.target.value)}
                  placeholder="Write your message here..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 outline-none resize-none"
                  style={{
                    backgroundColor: currentTheme.colors.background.secondary,
                    borderColor: currentTheme.colors.border.primary,
                    color: currentTheme.colors.text.primary,
                  }}
                />
              </div>

              {/* Send button */}
              <div className="flex justify-end">
                <button
                  disabled={pushSending || pushRoles.length === 0 || !pushTitle.trim() || !pushBody.trim()}
                  onClick={async () => {
                    if (!activeRestaurantId) { toast.error('No active restaurant selected'); return; }
                    setPushSending(true);
                    try {
                      const tokenCount = await webFcmService.sendByRole(activeRestaurantId, pushRoles, pushTitle.trim(), pushBody.trim());
                      if (tokenCount === 0) {
                        toast.error('No registered devices found for selected roles. Users must be logged in with notifications enabled.');
                      } else {
                        toast.success(`Notification sent to ${tokenCount} device${tokenCount > 1 ? 's' : ''}!`);
                        setPushRoles([]);
                        setPushTitle('');
                        setPushBody('');
                      }
                    } catch {
                      toast.error('Failed to send notification — check backend logs.');
                    } finally {
                      setPushSending(false);
                    }
                  }}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}
                >
                  <Send className="h-4 w-4" />
                  {pushSending ? 'Sending...' : 'Send Notification'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Register User Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
          <div className="rounded-lg sm:rounded-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto" style={{
            backgroundColor: currentTheme.colors.background.card
          }}>
            <div className="p-4 sm:p-6 border-b sticky top-0 z-10" style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.card }}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
                  <UserPlus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
                  Register New User
                </h2>
                <button
                  onClick={() => {
                    setShowRegisterModal(false);
                    reset();
                    setShowPassword(false);
                  }}
                  className="p-1.5 sm:p-2 rounded-lg transition-colors"
                  style={{ backgroundColor: currentTheme.colors.background.secondary }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: currentTheme.colors.text.primary }} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmitRegister)} className="p-4 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* First Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    {...register('firstName', { required: 'First name is required' })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: errors.firstName ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.firstName.message}</p>
                  )}
                </div>

                {/* Middle Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Middle Name
                  </label>
                  <input
                    type="text"
                    {...register('middleName')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                    placeholder="Enter middle name"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    {...register('lastName', { required: 'Last name is required' })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: errors.lastName ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.lastName.message}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: errors.email ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                    placeholder="Enter email address"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>

                {/* Mobile */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    {...register('mobile', {
                      required: 'Mobile number is required',
                      pattern: {
                        value: /^[6-9]\d{9}$/,
                        message: 'Enter a valid 10-digit mobile number'
                      }
                    })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: errors.mobile ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                    placeholder="Enter mobile number"
                    maxLength={10}
                  />
                  {errors.mobile && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.mobile.message}</p>
                  )}
                </div>

                {/* Date of Birth */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    {...register('dob', { required: 'Date of birth is required' })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: errors.dob ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                  />
                  {errors.dob && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.dob.message}</p>
                  )}
                </div>

                {/* Password */}
                <div className="md:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters'
                        }
                      })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 pr-10 sm:pr-12 text-sm"
                      style={{
                        borderColor: errors.password ? '#ef4444' : currentTheme.colors.border.primary,
                        backgroundColor: currentTheme.colors.background.secondary,
                        color: currentTheme.colors.text.primary
                      }}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
                      style={{ color: currentTheme.colors.text.secondary }}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" /> : <Eye className="h-4 w-4 sm:h-5 sm:w-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.password.message}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowRegisterModal(false);
                    reset();
                    setShowPassword(false);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || registerUserMutation.isLoading}
                  className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  style={{
                    backgroundColor: currentTheme.colors.primary[600]
                  }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                  onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                >
                  {isSubmitting || registerUserMutation.isLoading ? (
                    <ButtonLoader text="Registering..." />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Register User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        type={confirmationModal.type}
        isLoading={
          deleteUserMutation.isLoading ||
          deactivateUserMutation.isLoading ||
          activateUserMutation.isLoading ||
          deletePrintConfigMutation.isLoading
        }
      />

      {/* Print Configuration Modal */}
      {showPrintConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
          <div className="rounded-lg sm:rounded-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto" style={{
            backgroundColor: currentTheme.colors.background.card
          }}>
            <div className="p-4 sm:p-6 border-b sticky top-0 z-10" style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.card }}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
                  <Printer className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
                  {editingPrintConfig ? 'Edit Print Configuration' : 'Add Print Configuration'}
                </h2>
                <button
                  onClick={() => {
                    setShowPrintConfigModal(false);
                    setEditingPrintConfig(null);
                    resetPrintConfig();
                  }}
                  className="p-1.5 sm:p-2 rounded-lg transition-colors"
                  style={{ backgroundColor: currentTheme.colors.background.secondary }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: currentTheme.colors.text.primary }} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitPrintConfig(onSubmitPrintConfig)} className="p-4 sm:p-6">
              {!editingPrintConfig && (
                <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: currentTheme.colors.primary[50], border: `1px solid ${currentTheme.colors.primary[200]}` }}>
                  <p className="text-xs sm:text-sm" style={{ color: currentTheme.colors.primary[700] }}>
                    {!hasKOTConfig && !hasBillConfig && 'You can add configurations for KOT and BILL.'}
                    {hasKOTConfig && !hasBillConfig && 'KOT configuration already exists. You can add a BILL configuration.'}
                    {!hasKOTConfig && hasBillConfig && 'BILL configuration already exists. You can add a KOT configuration.'}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Print Type */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Print Type *
                  </label>
                  <select
                    {...registerPrintConfig('printType', { required: 'Print type is required' })}
                    disabled={!!editingPrintConfig}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      borderColor: printConfigErrors.printType ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                  >
                    <option value="">Select print type</option>
                    {(!editingPrintConfig && !hasKOTConfig) && (
                      <option value="KOT">KOT (Kitchen Order Ticket)</option>
                    )}
                    {(!editingPrintConfig && !hasBillConfig) && (
                      <option value="BILL">BILL (Customer Bill)</option>
                    )}
                    {editingPrintConfig && (
                      <>
                        <option value="KOT">KOT (Kitchen Order Ticket)</option>
                        <option value="BILL">BILL (Customer Bill)</option>
                      </>
                    )}
                  </select>
                  {printConfigErrors.printType && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{printConfigErrors.printType.message}</p>
                  )}
                  {editingPrintConfig && (
                    <p className="text-xs mt-1" style={{ color: currentTheme.colors.text.secondary }}>
                      Print type cannot be changed when editing
                    </p>
                  )}
                </div>

                {/* Print Size */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Print Size *
                  </label>
                  <select
                    {...registerPrintConfig('printSize', { required: 'Print size is required' })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: printConfigErrors.printSize ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                  >
                    <option value="">Select print size</option>
                    <option value="INCH_2">2 Inch</option>
                    <option value="INCH_3">3 Inch</option>
                    <option value="INCH_4">4 Inch</option>
                    <option value="A4">A4 Paper</option>
                  </select>
                  {printConfigErrors.printSize && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{printConfigErrors.printSize.message}</p>
                  )}
                </div>

                {/* Restaurant Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Restaurant Name
                  </label>
                  <input
                    type="text"
                    {...registerPrintConfig('restaurantName')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                    placeholder="Enter restaurant name"
                  />
                </div>

                {/* Restaurant Phone */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Restaurant Phone
                  </label>
                  <input
                    type="tel"
                    {...registerPrintConfig('restaurantPhone')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                    placeholder="Enter phone number"
                  />
                </div>

                {/* Restaurant Address */}
                <div className="md:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Restaurant Address
                  </label>
                  <textarea
                    {...registerPrintConfig('restaurantAddress')}
                    rows={3}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                    placeholder="Enter restaurant address"
                  />
                </div>

                {/* GST Number */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    GST Number
                  </label>
                  <input
                    type="text"
                    {...registerPrintConfig('gstNumber')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                    placeholder="Enter GST number"
                  />
                </div>

                {/* Logo URL */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Logo URL
                  </label>
                  <input
                    type="text"
                    {...registerPrintConfig('logoUrl')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                    placeholder="Enter logo URL"
                  />
                </div>
              </div>

              {/* KOT Specific Options - Only show when printType is KOT */}
              {(selectedPrintType === 'KOT' || editingPrintConfig?.printType === 'KOT') && (
                <div className="mt-4 p-4 rounded-lg border" style={{
                  backgroundColor: currentTheme.colors.background.secondary,
                  borderColor: currentTheme.colors.border.primary
                }}>
                  <h3 className="text-sm font-medium mb-3" style={{ color: currentTheme.colors.text.primary }}>
                    KOT Settings
                  </h3>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...registerPrintConfig('singleKotForAllKitchens')}
                      id="singleKotForAllKitchens"
                      className="h-4 w-4 rounded border-gray-300 focus:ring-2"
                      style={{ accentColor: currentTheme.colors.primary[600] }}
                    />
                    <label
                      htmlFor="singleKotForAllKitchens"
                      className="ml-2 text-sm"
                      style={{ color: currentTheme.colors.text.primary }}
                    >
                      One KOT for all kitchens
                    </label>
                  </div>
                  <p className="mt-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                    When enabled, a single KOT will be printed for all items instead of splitting by kitchen.
                  </p>
                </div>
              )}

              {/* BILL Specific Options - Only show when printType is BILL */}
              {(selectedPrintType === 'BILL' || editingPrintConfig?.printType === 'BILL') && (
                <div className="mt-4 p-4 rounded-lg border" style={{
                  backgroundColor: currentTheme.colors.background.secondary,
                  borderColor: currentTheme.colors.border.primary
                }}>
                  <h3 className="text-sm font-medium mb-3" style={{ color: currentTheme.colors.text.primary }}>
                    BILL Settings
                  </h3>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...registerPrintConfig('gstApplicable')}
                      id="gstApplicable"
                      className="h-4 w-4 rounded border-gray-300 focus:ring-2"
                      style={{ accentColor: currentTheme.colors.primary[600] }}
                    />
                    <label
                      htmlFor="gstApplicable"
                      className="ml-2 text-sm"
                      style={{ color: currentTheme.colors.text.primary }}
                    >
                      GST Applicable in Customer Bill
                    </label>
                  </div>
                  <p className="mt-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                    When enabled, GST details (CGST/SGST) will be shown in bills and order screens.
                  </p>

                  <div className="mt-4">
                    <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                      UPI ID (for QR code on bill)
                    </label>
                    <input
                      type="text"
                      {...registerPrintConfig('upiId')}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                      style={{
                        borderColor: currentTheme.colors.border.primary,
                        backgroundColor: currentTheme.colors.background.card,
                        color: currentTheme.colors.text.primary
                      }}
                      placeholder="e.g., merchant@upi"
                    />
                    <p className="mt-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                      When provided, a UPI payment QR code with the bill amount will be printed.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPrintConfigModal(false);
                    setEditingPrintConfig(null);
                    resetPrintConfig();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isPrintConfigSubmitting || savePrintConfigMutation.isLoading}
                  className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  style={{
                    backgroundColor: currentTheme.colors.primary[600]
                  }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                  onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                >
                  {isPrintConfigSubmitting || savePrintConfigMutation.isLoading ? (
                    <ButtonLoader text={editingPrintConfig ? "Updating..." : "Saving..."} />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingPrintConfig ? 'Update Configuration' : 'Save Configuration'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
